import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Add } from '@mui/icons-material'
import { Alert, Box, Button, Card, Stack, TableContainer, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn, SortOrder } from '../../../../../components/GRTable/GRTable'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import {
  useCreateInternalRoleMutation,
  useDeleteInternalRoleMutation,
  useGetInternalFeatureModulesQuery,
  useGetInternalPackagesQuery,
  useGetInternalRolesQuery,
  useUpdateInternalRoleMutation,
} from '../../../../api/plans'
import { QueryStateBoundary } from '../../../../components/QueryStateBoundary/QueryStateBoundary'
import { rowActionsColumn } from '../../../../components/RowActions/RowActions'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import { InternalRole, RoleCreate } from '../../types/Plans'
import { RoleEditDialog } from '../RoleEditDialog/RoleEditDialog'
import { getRoleUsage, isRoleDescriptionStored, RoleFormValues, toRoleCreate, toRoleUpdate } from '../helpers/planHelpers'

type CustomProps = {
  onEdit: (role: InternalRole) => void
  onDelete: (role: InternalRole) => void
}

type DialogState = { open: boolean; role?: InternalRole }

export const RolesTab: FC = () => {
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const rolesQuery = useGetInternalRolesQuery()
  const packagesQuery = useGetInternalPackagesQuery()
  const modulesQuery = useGetInternalFeatureModulesQuery()
  const [createRole, { isLoading: isCreating }] = useCreateInternalRoleMutation()
  const [updateRole, { isLoading: isUpdating }] = useUpdateInternalRoleMutation()
  const [deleteRole] = useDeleteInternalRoleMutation()
  const [dialogState, setDialogState] = useState<DialogState>({ open: false })
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<InternalRole>>()

  const roles = useMemo(() => rolesQuery.data ?? [], [rolesQuery.data])
  const packages = useMemo(() => packagesQuery.data ?? [], [packagesQuery.data])
  const modules = useMemo(() => modulesQuery.data ?? [], [modulesQuery.data])

  const failureMessages = (key: string) => ({ error: t(key), errorWithReason: (reason: string) => `${t(key)}: ${reason}` })

  const tableColumns: GRTableColumn<InternalRole, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-csm:plans_role_name'),
        accessor: ({ row }) => <Typography fontWeight="bold">{row.name}</Typography>,
        sortable: true,
        sortAccessor: ({ row }) => row.name.toLowerCase(),
        // GRTable compares strings in reverse, so DESC lists names A to Z.
        sortOrder: SortOrder.DESC,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 260 } },
      },
      {
        labelAccessor: t('internal-csm:plans_description'),
        accessor: ({ row }) => row.description,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 320 } },
      },
      rowActionsColumn<InternalRole, CustomProps>({
        actions: ({ row, customTableProps }) => [
          { type: 'edit', label: t('internal-csm:plans_edit_role'), onClick: () => customTableProps?.onEdit(row) },
          { type: 'delete', label: t('internal-csm:plans_delete_role'), onClick: () => customTableProps?.onDelete(row) },
        ],
      }),
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({
      onEdit: (role) => setDialogState({ open: true, role }),
      onDelete: (role) => {
        const usage = getRoleUsage(role.name, packages, modules)
        setConfirmDialogData({
          title: t('internal-csm:plans_delete_role'),
          confirmText: (
            <Stack spacing={2}>
              <Typography>{t('internal-csm:plans_delete_role_confirm', { name: role.name })}</Typography>
              <Alert severity="warning">
                <Stack spacing={1}>
                  <Typography variant="body2">{t('internal-csm:plans_delete_role_warning')}</Typography>
                  <Typography variant="body2">
                    {t('internal-csm:plans_role_used_by_packages', { names: usage.packages.join(', ') || t('internal-csm:plans_none') })}
                  </Typography>
                  <Typography variant="body2">
                    {t('internal-csm:plans_role_used_by_modules', { names: usage.modules.join(', ') || t('internal-csm:plans_none') })}
                  </Typography>
                </Stack>
              </Alert>
            </Stack>
          ),
          actionText: t('internal-common:delete'),
          destructiveAction: true,
          data: role,
        })
      },
    }),
    [modules, packages, t]
  )

  const reportStored = (sent: RoleCreate, stored?: InternalRole) => {
    if (!stored) {
      notifyError(t('internal-csm:plans_role_not_found_after_save', { name: sent.name }))
      return false
    }
    if (!isRoleDescriptionStored(sent, stored)) {
      notifyError(t('internal-csm:plans_not_stored', { fields: t('internal-csm:plans_description') }))
      return false
    }
    return true
  }

  const handleSave = async (values: RoleFormValues) => {
    const storedRole = dialogState.role
    if (storedRole) {
      const role = toRoleUpdate(storedRole, values)
      const updated = await run(updateRole(role).unwrap(), failureMessages('internal-csm:plans_role_save_failed'))
      if (!updated.ok || !reportStored(role, updated.result.stored)) return
      notifySuccess(t('internal-csm:plans_role_saved', { name: role.name }))
    } else {
      const role = toRoleCreate(values)
      const created = await run(createRole(role).unwrap(), failureMessages('internal-csm:plans_role_save_failed'))
      if (!created.ok || !reportStored(role, created.result.stored)) return
      notifySuccess(t('internal-csm:plans_role_added', { name: role.name }))
    }
    setDialogState({ open: false })
  }

  const handleConfirmDelete = async (confirmed?: ConfirmDialogData<InternalRole>) => {
    setConfirmDialogData(undefined)
    const role = confirmed?.data
    if (!role) return
    const deleted = await run(deleteRole(role.name).unwrap(), failureMessages('internal-csm:plans_role_delete_failed'))
    if (!deleted.ok) return
    if (deleted.result.stored) {
      notifyError(t('internal-csm:plans_role_not_deleted', { name: role.name }))
      return
    }
    notifySuccess(t('internal-csm:plans_role_deleted', { name: role.name }))
  }

  const isLoading = rolesQuery.isLoading || packagesQuery.isLoading || modulesQuery.isLoading
  const isError = rolesQuery.isError || packagesQuery.isError || modulesQuery.isError

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          {t('internal-csm:plans_role_count', { count: roles.length })}
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialogState({ open: true })} disabled={isLoading || isError}>
          {t('internal-csm:plans_add_role')}
        </Button>
      </Box>

      <QueryStateBoundary isLoading={isLoading} isError={isError} errorText={t('internal-csm:plans_load_failed')}>
        <TableContainer component={Card}>
          <GRTable
            rows={roles}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey="id"
            customProps={customProps}
            noRowsLabel={t('internal-common:no_rows')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      <RoleEditDialog
        open={dialogState.open}
        role={dialogState.role}
        roles={roles}
        isSaving={isCreating || isUpdating}
        onClose={() => setDialogState({ open: false })}
        onSave={handleSave}
      />
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirmDelete} />
    </Box>
  )
}
